import { Link } from "react-router-dom";
import { Badge, Button, ErrorState, LoadingState } from "@odingard/design-system";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { routeMeta, usePageMeta } from "../../lib/usePageMeta";
import { AppCard, SHELVES, byAvailability } from "./Marketplace";
import { ProductShot } from "./ProductShot";
import { CERTIFICATION_STATUS } from "./trust";

// The storefront is rendered from the catalog registry, so a new product reaches
// the front page through a catalog row — never a code change here.
export function Home() {
  usePageMeta(routeMeta("/"));

  const store = useAsync(async () => {
    const [apps, bundles, categories] = await Promise.all([
      api.catalogApplications({ pageSize: 100 }),
      api.catalogBundles(),
      api.catalogCategories(),
    ]);
    return { apps: apps.applications, bundles: bundles.bundles, categories: categories.categories };
  }, []);

  const apps = store.data?.apps ?? [];
  const applications = apps.filter((a) => a.delivery === "platform").sort(byAvailability);
  const bundles = store.data?.bundles ?? [];
  const categories = store.data?.categories ?? [];

  return (
    <>
      <section className="hero">
        <div className="container">
          <span className="hero__eyebrow">Odingard Studio</span>
          <h1>Win more government contracts without adding audit risk.</h1>
          <p className="lede">
            Governed AI for GovCon and regulated teams: contracts, proposals and policy in one account, with grounded
            cited output, tenant isolation and audit ready evidence. When a solicitation drops, build a compliant
            response fast, and keep the evidence that proves how you got there.
          </p>
          <div className="ods-row">
            <Link to="/start">
              <Button size="lg">Start free trial</Button>
            </Link>
            <Link to="/marketplace">
              <Button variant="secondary" size="lg">
                Browse the marketplace
              </Button>
            </Link>
          </div>
          <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", marginTop: "var(--ods-space-3)" }}>
            See real prices and start free today: no sales call, no per seat surprise. One firm, one price, every user.
          </p>
        </div>
      </section>

      <ProductShot />

      <div className="trust-band">
        <div className="container">
          <span className="trust-chip">🔒 Tenant isolated</span>
          <span className="trust-chip">✦ Grounded AI with citations</span>
          <span className="trust-chip">📜 Hash chained audit</span>
          <Link to="/marketplace/updates" className="trust-chip">
            🗓 What shipped recently
          </Link>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="section__head">
            <h2>What happens to your data, and what we are actually certified for</h2>
            <p>
              Your documents and prompts are never used to train public or shared AI models, and they are never used to
              train a model for another customer. Every record is scoped to one organization, and the model provider
              receives only the text a request needs.
            </p>
          </div>
          <div className="feature-grid">
            {CERTIFICATION_STATUS.map((c) => (
              <div className="feature" key={c.framework}>
                <h3>{c.framework}</h3>
                <p>
                  <strong>{c.status}</strong> {c.detail}
                </p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: "var(--ods-space-4)" }}>
            <Link to="/security">How Odingard Studio handles your data and evidence</Link>
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head">
            <h2>{SHELVES.apps}</h2>
            <p>
              Every Odingard AI application in one place, with one account, one bill and one audit trail. Start a free trial
              from any product page; no card required to explore.
            </p>
          </div>
          {categories.length > 0 && (
            <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap", marginBottom: "var(--ods-space-5)" }}>
              {categories.map((c) => (
                <Link key={c.key} to={`/marketplace?category=${encodeURIComponent(c.key)}`}>
                  <Button variant="secondary" size="sm">
                    {c.name}
                  </Button>
                </Link>
              ))}
            </div>
          )}
          {store.loading && <LoadingState label="Loading the catalog…" />}
          {store.error && <ErrorState description={store.error.message} onRetry={store.reload} />}
          {store.data && (
            <div className="feature-grid">
              {applications.map((a) => (
                <AppCard app={a} key={a.id} />
              ))}
            </div>
          )}
        </div>
      </section>

      {bundles.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section__head">
              <h2>{SHELVES.bundles}</h2>
              <p>One firm, one price, every user. Expand from a single application into the whole suite.</p>
            </div>
            <div className="feature-grid">
              {bundles.map((b) => (
                <Link
                  key={b.id}
                  to={`/marketplace/bundles/${b.slug}`}
                  className="feature"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <div className="ods-row" style={{ justifyContent: "space-between" }}>
                    <div className="feature__icon" aria-hidden="true">
                      {b.kind === "addon" ? "🛡️" : "📦"}
                    </div>
                    {b.lifecycleStatus === "coming_soon" ? (
                      <Badge tone="neutral">Coming soon</Badge>
                    ) : (
                      <Badge tone="success">Available</Badge>
                    )}
                  </div>
                  <h3>{b.name}</h3>
                  <p>{b.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="container">
          <div className="ods-card">
            <div
              className="ods-card__body"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "var(--ods-space-5)",
                flexWrap: "wrap",
              }}
            >
              <div>
                <h2 style={{ margin: "0 0 8px" }}>Ready to see it in your workflow?</h2>
                <p style={{ margin: 0, color: "var(--ods-color-muted)" }}>Start a free trial. No card required to explore.</p>
              </div>
              <Link to="/start">
                <Button size="lg">Start free trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
